import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useDarkMode } from "../contexts/DarkModeContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

const PrivacyPolicy = () => {
  const handleScrollToSection = (sectionId) => {
    // Navigate to home page with section
    window.location.href = `/#${sectionId}`;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Navbar scrollToSection={handleScrollToSection} activeSection="" />

      <div className="pt-16 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Last updated: July 22, 2025
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            <section className="mb-8">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                This Privacy Policy describes Our policies and procedures on the
                collection, use and disclosure of Your information when You use
                the Service and tells You about Your privacy rights and how the
                law protects You.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use Your Personal data to provide and improve the Service. By
                using the Service, You agree to the collection and use of
                information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                INTERPRETATION AND DEFINITIONS
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Interpretation
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The words of which the initial letter is capitalized have
                meanings defined under the following conditions. The following
                definitions shall have the same meaning regardless of whether
                they appear in singular or in plural.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Definitions
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                For the purposes of this Privacy Policy:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>Account</strong> means a unique account created for
                  You to access our Service or parts of our Service.
                </li>
                <li>
                  <strong>Business</strong>, for the purpose of the CCPA
                  (California Consumer Privacy Act), refers to the Company as
                  the legal entity that collects Consumers' personal information
                  and determines the purposes and means of the processing of
                  Consumers' personal information, or on behalf of which such
                  information is collected and that alone, or jointly with
                  others, determines the purposes and means of the processing of
                  consumers' personal information, that does business in the
                  State of California.
                </li>
                <li>
                  <strong>Company</strong> (referred to as either "the Company",
                  "We", "Us" or "Our" in this Agreement) refers to AI Ventures
                  Management, LLC, Offices 195 Page Mill Road Suite 115, Palo
                  Alto, CA 94306.
                </li>
                <li>
                  <strong>Consumer</strong>, for the purpose of the CCPA
                  (California Consumer Privacy Act), means a natural person who
                  is a California resident.
                </li>
                <li>
                  <strong>Cookies</strong> are small files that are placed on
                  Your computer, mobile device or any other device by a website,
                  containing the details of Your browsing history on that
                  website among its many uses.
                </li>
                <li>
                  <strong>Country</strong> refers to: California, United States
                </li>
                <li>
                  <strong>Data Controller</strong>, for the purposes of the GDPR
                  (General Data Protection Regulation), refers to the Company as
                  the legal person which alone or jointly with others determines
                  the purposes and means of the processing of Personal Data.
                </li>
                <li>
                  <strong>Device</strong> means any device that can access the
                  Service such as a computer, a cellphone or a digital tablet.
                </li>
                <li>
                  <strong>Personal Data</strong> is any information that relates
                  to an identified or identifiable individual.
                </li>
                <li>
                  <strong>Service</strong> refers to the Website and Buildathon
                  event application and registration system.
                </li>
                <li>
                  <strong>Service Provider</strong> means any natural or legal
                  person who processes the data on behalf of the Company.
                </li>
                <li>
                  <strong>Usage Data</strong> refers to data collected
                  automatically, either generated by the use of the Service or
                  from the Service infrastructure itself.
                </li>
                <li>
                  <strong>Website</strong> refers to AI Fund, accessible from
                  https://aifund.ai/
                </li>
                <li>
                  <strong>You</strong> means the individual accessing or using
                  the Service, or the company, or other legal entity on behalf
                  of which such individual is accessing or using the Service, as
                  applicable.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                COLLECTING AND USING YOUR PERSONAL DATA
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Types of Data Collected
              </h3>

              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Personal Data
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                While using Our Service, We may ask You to provide Us with
                certain personally identifiable information that can be used to
                contact or identify You. Personally identifiable information may
                include, but is not limited to:
              </p>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Contact Information:
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>LinkedIn profile URL</li>
                </ul>
              </div>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Professional Information:
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>
                    Role/position (AI Engineer, Backend Developer, Data
                    Engineer, Data Scientist, DevOps Engineer, Frontend
                    Developer, Full Stack Developer, Machine Learning Engineer,
                    Mobile Developer, Product Manager, Student, Technical
                    Leader, UX/UI Designer, Other)
                  </li>
                  <li>
                    Professional background (Entrepreneur, Executive/Management,
                    Individual Contributor, Student)
                  </li>
                  <li>Years of professional experience</li>
                  <li>Resume/CV (PDF files, optional)</li>
                </ul>
              </div>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Event-Related Information:
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>Event goals and participation preferences</li>
                  <li>T-shirt size</li>
                  <li>Dietary restrictions and food allergies</li>
                  <li>How you heard about the event</li>
                  <li>
                    Team participation preferences and team member information
                  </li>
                  <li>Attendance confirmation</li>
                </ul>
              </div>

              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Usage Data
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Usage Data is collected automatically when using the Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Usage Data may include information such as Your Device's
                Internet Protocol address (e.g. IP address), browser type,
                browser version, the pages of our Service that You visit, the
                time and date of Your visit, the time spent on those pages,
                unique device identifiers and other diagnostic data.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                When You access the Service by or through a mobile device, We
                may collect certain information automatically, including, but
                not limited to, the type of mobile device You use, Your mobile
                device unique ID, the IP address of Your mobile device, Your
                mobile operating system, the type of mobile Internet browser You
                use, unique device identifiers and other diagnostic data.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Tracking Technologies and Cookies
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use Cookies and similar tracking technologies to track the
                activity on Our Service and store certain information. Tracking
                technologies used are beacons, tags, and scripts to collect and
                track information and to improve and analyze Our Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use both Session and Persistent Cookies for the purposes set
                out below:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>Necessary / Essential Cookies:</strong> These Cookies
                  are essential to provide You with services available through
                  the Website and to enable You to use some of its features.
                </li>
                <li>
                  <strong>Cookies Policy / Notice Acceptance Cookies:</strong>{" "}
                  These Cookies identify if users have accepted the use of
                  cookies on the Website.
                </li>
                <li>
                  <strong>Functionality Cookies:</strong> These Cookies allow us
                  to remember choices You make when You use the Website, such as
                  remembering your login details or language preference.
                </li>
                <li>
                  <strong>Tracking and Performance Cookies:</strong> These
                  Cookies are used to track information about traffic to the
                  Website and how users use the Website.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Use of Your Personal Data
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The Company may use Personal Data for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>To provide and maintain our Service,</strong>{" "}
                  including processing and managing your Buildathon application
                </li>
                <li>
                  <strong>To manage Your Account:</strong> to manage Your
                  registration as a user of the Service
                </li>
                <li>
                  <strong>To contact You:</strong> To contact You by email,
                  telephone calls, SMS, or other equivalent forms of electronic
                  communication regarding event updates, schedules, and
                  important information
                </li>
                <li>
                  <strong>For event operations:</strong> To facilitate team
                  formation, networking opportunities, provide necessary
                  accommodations and event materials
                </li>
                <li>
                  <strong>To provide You</strong> with news, special offers and
                  general information about other events, services and
                  opportunities which we offer
                </li>
                <li>
                  <strong>To manage Your requests:</strong> To attend and manage
                  Your requests to Us
                </li>
                <li>
                  <strong>For business transfers:</strong> We may use Your
                  information to evaluate or conduct a merger, divestiture,
                  restructuring, reorganization, dissolution, or other sale or
                  transfer of some or all of Our assets
                </li>
                <li>
                  <strong>For other purposes:</strong> We may use Your
                  information for data analysis, identifying usage trends,
                  determining the effectiveness of our promotional campaigns and
                  to evaluate and improve our Service, products, services,
                  marketing and your experience
                </li>
              </ul>

              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may share Your personal information in the following
                situations:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>With Service Providers:</strong> We may share Your
                  personal information with Service Providers to monitor and
                  analyze the use of our Service, to contact You
                </li>
                <li>
                  <strong>With Event Partners:</strong> With sponsors and
                  partners for networking and recruitment purposes{" "}
                </li>
                <li>
                  <strong>For business transfers:</strong> We may share or
                  transfer Your personal information in connection with, or
                  during negotiations of, any merger, sale of Company assets,
                  financing, or acquisition
                </li>
                <li>
                  <strong>With Affiliates:</strong> We may share Your
                  information with Our affiliates
                </li>
                <li>
                  <strong>With business partners:</strong> We may share Your
                  information with Our business partners to offer You certain
                  products, services or promotions
                </li>
                <li>
                  <strong>With team members:</strong> Your contact information
                  may be shared with your team members for collaboration
                  purposes
                </li>
                <li>
                  <strong>With Your consent:</strong> We may disclose Your
                  personal information for any other purpose with Your consent
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Retention of Your Personal Data
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The Company will retain Your Personal Data only for as long as
                is necessary for the purposes set out in this Privacy Policy. We
                will retain and use Your Personal Data to the extent necessary
                to comply with our legal obligations, resolve disputes, and
                enforce our legal agreements and policies.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Transfer of Your Personal Data
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Your information, including Personal Data, is processed at the
                Company's operating offices and in any other places where the
                parties involved in the processing are located. The Company will
                take all steps reasonably necessary to ensure that Your data is
                treated securely and in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Disclosure of Your Personal Data
              </h3>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Business Transactions
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If the Company is involved in a merger, acquisition or asset
                sale, Your Personal Data may be transferred.
              </p>

              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Law enforcement
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Under certain circumstances, the Company may be required to
                disclose Your Personal Data if required to do so by law or in
                response to valid requests by public authorities.
              </p>

              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Other legal requirements
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The Company may disclose Your Personal Data in the good faith
                belief that such action is necessary to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Comply with a legal obligation</li>
                <li>
                  Protect and defend the rights or property of the Company
                </li>
                <li>
                  Prevent or investigate possible wrongdoing in connection with
                  the Service
                </li>
                <li>
                  Protect the personal safety of Users of the Service or the
                  public
                </li>
                <li>Protect against legal liability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Security of Your Personal Data
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                The security of Your Personal Data is important to Us, but
                remember that no method of transmission over the Internet, or
                method of electronic storage is 100% secure. While We strive to
                use commercially acceptable means to protect Your Personal Data,
                We cannot guarantee its absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                DETAILED INFORMATION ON THE PROCESSING OF YOUR PERSONAL DATA
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Analytics
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use third-party Service providers to monitor and analyze
                the use of our Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Google Analytics</strong> Google Analytics is a web
                analytics service offered by Google that tracks and reports
                website traffic. You can opt-out of having made your activity on
                the Service available to Google Analytics by installing the
                Google Analytics opt-out browser add-on.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Email Marketing
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use Your Personal Data to contact You with newsletters,
                marketing or promotional materials and other information that
                may be of interest to You. You may opt-out of receiving any, or
                all, of these communications from Us by following the
                unsubscribe link or instructions provided in any email We send.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Mailchimp</strong> Mailchimp is an email marketing
                sending service provided by The Rocket Science Group LLC.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Usage, Performance and Miscellaneous
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Invisible reCAPTCHA</strong> We use an invisible captcha
                service named reCAPTCHA operated by Google for security
                purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                GDPR PRIVACY
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Legal Basis for Processing Personal Data under GDPR
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may process Personal Data under the following conditions:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>Consent:</strong> You have given Your consent for
                  processing Personal Data for one or more specific purposes
                </li>
                <li>
                  <strong>Performance of a contract:</strong> Provision of
                  Personal Data is necessary for the performance of an agreement
                  with You
                </li>
                <li>
                  <strong>Legal obligations:</strong> Processing Personal Data
                  is necessary for compliance with a legal obligation
                </li>
                <li>
                  <strong>Vital interests:</strong> Processing Personal Data is
                  necessary in order to protect Your vital interests
                </li>
                <li>
                  <strong>Public interests:</strong> Processing Personal Data is
                  related to a task that is carried out in the public interest
                </li>
                <li>
                  <strong>Legitimate interests:</strong> Processing Personal
                  Data is necessary for the purposes of the legitimate interests
                  pursued by the Company
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Your Rights under the GDPR
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You have the right under this Privacy Policy, and by law if You
                are within the EU, to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Request access to Your Personal Data</li>
                <li>
                  Request correction of the Personal Data that We hold about You
                </li>
                <li>Object to processing of Your Personal Data</li>
                <li>Request erasure of Your Personal Data</li>
                <li>Request the transfer of Your Personal Data</li>
                <li>Withdraw Your consent</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                CCPA PRIVACY
              </h2>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Categories of Personal Information Collected
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We collect information that identifies, relates to, describes,
                references, is capable of being associated with, or could
                reasonably be linked, directly or indirectly, with a particular
                Consumer or Device.
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>
                  <strong>Category A: Identifiers</strong> Examples: Real name,
                  email address, phone number, LinkedIn profile URL{" "}
                  <em>Collected: Yes</em>
                </li>
                <li>
                  <strong>
                    Category B: Personal information categories listed in the
                    California Customer Records statute
                  </strong>{" "}
                  Examples: Name, address, telephone number, employment
                  information, education information <em>Collected: Yes</em>
                </li>
                <li>
                  <strong>
                    Category F: Internet or other similar network activity
                  </strong>{" "}
                  Examples: Interaction with our Service or advertisement{" "}
                  <em>Collected: Yes</em>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Use of Personal Information for Business Purposes or Commercial
                Purposes
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may use or disclose personal information We collect for
                "business purposes" or "commercial purposes" including:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>To operate our Service and provide You with our Service</li>
                <li>
                  To provide You with support and to respond to Your inquiries
                </li>
                <li>
                  To fulfill or meet the reason You provided the information
                </li>
                <li>
                  To respond to law enforcement requests and as required by
                  applicable law
                </li>
                <li>For internal administrative and auditing purposes</li>
                <li>
                  To detect security incidents and protect against malicious,
                  deceptive, fraudulent or illegal activity
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Your Rights under the CCPA
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If You are a resident of California, You have the following
                rights:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>The right to notice</li>
                <li>
                  The right to request disclosure of information about Our
                  collection, use, sale, disclosure for business purposes and
                  share of personal information
                </li>
                <li>
                  The right to say no to the sale of Personal Data (opt-out)
                </li>
                <li>The right to delete Personal Data</li>
                <li>The right not to be discriminated against</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Exercising Your CCPA Data Protection Rights
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You can contact Us by email: privacy@aifund.ai
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                CHILDREN'S PRIVACY
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Our Service does not address anyone under the age of 13. We do
                not knowingly collect personally identifiable information from
                anyone under the age of 13. If You are a parent or guardian and
                You are aware that Your child has provided Us with Personal
                Data, please contact Us.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                LINKS TO OTHER WEBSITES
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Our Service may contain links to other websites that are not
                operated by Us. We strongly advise You to review the Privacy
                Policy of every site You visit. We have no control over and
                assume no responsibility for the content, privacy policies or
                practices of any third party sites or services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                CHANGES TO THIS PRIVACY POLICY
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We may update Our Privacy Policy from time to time. We will
                notify You of any changes by posting the new Privacy Policy on
                this page and updating the "Last updated" date at the top of
                this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                CONTACT US
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, You can
                contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  By email:{" "}
                  <a
                    href="mailto:privacy@aifund.ai"
                    className="text-aifund hover:text-aifund-dark"
                  >
                    privacy@aifund.ai
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-8 border-t border-gray-200 dark:border-gray-600 pt-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                COOKIES POLICY
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Last updated: July 22, 2025
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                This Cookies Policy explains what Cookies are and how We use
                them. You should read this policy so You can understand what
                type of cookies We use, or the information We collect using
                Cookies and how that information is used.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                The use of the Cookies
              </h3>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Type of Cookies We Use
              </h4>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Cookies can be "Persistent" or "Session" Cookies. Persistent
                Cookies remain on your personal computer or mobile device when
                You go offline, while Session Cookies are deleted as soon as You
                close your web browser.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We use persistent Cookies for the purposes set out below:
              </p>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Functionality Cookies
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>
                    <strong>Type:</strong> Persistent Cookies
                  </li>
                  <li>
                    <strong>Administered by:</strong> Us
                  </li>
                  <li>
                    <strong>Purpose:</strong> These Cookies allow us to remember
                    choices You make when You use the Website
                  </li>
                </ul>
              </div>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Tracking and Performance Cookies
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>
                    <strong>Type:</strong> Persistent Cookies
                  </li>
                  <li>
                    <strong>Administered by:</strong> Third-Parties
                  </li>
                  <li>
                    <strong>Purpose:</strong> These Cookies are used to track
                    information about traffic to the Website and how users use
                    the Website
                  </li>
                </ul>
              </div>

              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-2">
                  Targeting and Advertising Cookies
                </h5>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-1 ml-4">
                  <li>
                    <strong>Type:</strong> Persistent Cookies
                  </li>
                  <li>
                    <strong>Administered by:</strong> Third-Parties
                  </li>
                  <li>
                    <strong>Purpose:</strong> These Cookies track your browsing
                    habits to enable Us to show advertising which is more likely
                    to be of interest to You
                  </li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Your Choices Regarding Cookies
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If You prefer to avoid the use of Cookies on the Website, first
                You must disable the use of Cookies in your browser and then
                delete the Cookies saved in your browser associated with this
                website.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Contact Us
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have any questions about this Cookies Policy, You can
                contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-gray-700 dark:text-gray-300">
                  By email:{" "}
                  <a
                    href="mailto:privacy@aifund.ai"
                    className="text-aifund hover:text-aifund-dark"
                  >
                    privacy@aifund.ai
                  </a>
                </p>
              </div>
            </section>
          </motion.div>

          {/* Back to Home */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/"
              className="inline-flex items-center text-aifund hover:text-aifund-dark transition-colors duration-200 font-medium"
            >
              ← Back to Home
            </Link>
          </motion.div>
        </div>
      </div>

      <Footer />

      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default PrivacyPolicy;
